import { useState, useRef, useCallback } from 'react';

interface UseCountUpOptions {
    number: number; // 目标数字
    time: number; // 动画时长 (ms)
}

export function useCountUp({ number, time }: UseCountUpOptions) {
    const [curNumber, setCurNumber] = useState('0,000');
    const rafRef = useRef<number | null>(null);

    // 获取当前数字（去掉千分位）
    const getCurrentValue = () => parseInt(curNumber.replace(/,/g, ''), 10) || 0;

    // 格式化（补零 + 千分位）
    const format = (val: number, digitLength: number) => {
        const raw = String(Math.floor(val)).padStart(digitLength, '0');
        return raw.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };

    const run = useCallback(() => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);

        const start = performance.now();
        const startValue = 0;
        const digitLength = String(number).length;

        const step = (now: number) => {
            const progress = Math.min((now - start) / time, 1);
            const value = startValue + (number - startValue) * progress;
            setCurNumber(format(value, digitLength));

            if (progress < 1) {
                rafRef.current = requestAnimationFrame(step);
            }
        };

        rafRef.current = requestAnimationFrame(step);
    }, [number, time]);

    const reset = useCallback(() => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);

        const start = performance.now();
        const startValue = getCurrentValue();
        const digitLength = String(number).length;

        const step = (now: number) => {
            const progress = Math.min((now - start) / time, 1);
            const value = startValue * (1 - progress); // 从当前值 -> 0
            setCurNumber(format(value, digitLength));

            if (progress < 1) {
                rafRef.current = requestAnimationFrame(step);
            }
        };

        rafRef.current = requestAnimationFrame(step);
    }, [curNumber, number, time]);

    return { curNumber, run, reset };
}
